import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  Download,
  FileText,
  Loader2,
  Trash2,
  Upload,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { fetchBlobWithAuth, fetchWithAuth } from "@/lib/api/client";
import { useDocumentProcessingStatus } from "@/hooks/use-documents";
import { showToast } from "@/lib/toast";

// Mejora "Docs por familia" (changelog cliente): bloque reusable para
// cada una de las dos secciones de la página /documentos. Cada bloque
// gestiona su listado, subida (con tipo seleccionable dentro de la
// familia), polling del procesamiento, descarga y borrado.

export type Familia = "bases" | "apoyo";

export interface TipoOption {
  value: string;
  label: string;
}

interface Documento {
  id: string;
  nombre: string;
  nombre_original?: string;
  tipo: string;
  familia?: string;
  tamanio: number;
  estado_procesamiento: string;
  processing_error?: string | null;
  created_at: string;
}

interface DocumentSectionProps {
  licitacionId: string;
  familia: Familia;
  title: string;
  description: string;
  icon: LucideIcon;
  tipoOptions: TipoOption[];
  defaultTipo: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const estadoProcessColors: Record<string, string> = {
  PENDIENTE: "bg-yellow-100 text-yellow-700",
  EN_PROCESO: "bg-blue-100 text-blue-700",
  EXTRAYENDO_TEXTO: "bg-blue-100 text-blue-700",
  GENERANDO_CHUNKS: "bg-blue-100 text-blue-700",
  GENERANDO_EMBEDDINGS: "bg-blue-100 text-blue-700",
  COMPLETADO: "bg-green-100 text-green-700",
  ERROR: "bg-red-100 text-red-700",
};

const processingStepLabel: Record<string, string> = {
  PENDIENTE: "En cola...",
  EN_PROCESO: "Procesando...",
  EXTRAYENDO_TEXTO: "Extrayendo texto...",
  GENERANDO_CHUNKS: "Generando chunks...",
  GENERANDO_EMBEDDINGS: "Generando embeddings...",
  COMPLETADO: "Completado",
  ERROR: "Error",
};

function DocumentProcessingProgress({
  docId,
  estado,
  onComplete,
  onError,
}: {
  docId: string;
  estado: string;
  onComplete: () => void;
  onError: (error: string | null) => void;
}) {
  const isProcessing = estado !== "COMPLETADO" && estado !== "ERROR";
  const { data: status } = useDocumentProcessingStatus(docId, isProcessing);

  const prevEstadoRef = useRef(estado);

  useEffect(() => {
    if (!status) return;
    if (
      status.estado_procesamiento === "COMPLETADO" &&
      prevEstadoRef.current !== "COMPLETADO"
    ) {
      prevEstadoRef.current = "COMPLETADO";
      onComplete();
    } else if (
      status.estado_procesamiento === "ERROR" &&
      prevEstadoRef.current !== "ERROR"
    ) {
      prevEstadoRef.current = "ERROR";
      onError(status.processing_error);
    }
  }, [status, onComplete, onError]);

  if (!isProcessing) return null;

  const progressPct = Math.round((status?.processing_progress ?? 0) * 100);
  const currentEstado = status?.estado_procesamiento ?? estado;
  const label = processingStepLabel[currentEstado] || "Procesando...";

  return (
    <div className="mt-1.5 w-full max-w-xs">
      <div className="flex items-center gap-2 mb-0.5">
        <Loader2 className="w-3 h-3 text-blue-500 animate-spin flex-shrink-0" />
        <span className="text-xs text-gray-500">{label}</span>
        {progressPct > 0 && (
          <span className="text-xs text-gray-400 ml-auto">{progressPct}%</span>
        )}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-1.5">
        <div
          className="bg-blue-500 h-1.5 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${Math.max(progressPct, 5)}%` }}
        />
      </div>
    </div>
  );
}

export function DocumentSection({
  licitacionId,
  familia,
  title,
  description,
  icon: Icon,
  tipoOptions,
  defaultTipo,
}: DocumentSectionProps) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // Tipo concreto con el que se subirá el próximo archivo (dropdown
  // limitado a los tipos de esta familia).
  const [selectedTipo, setSelectedTipo] = useState<string>(defaultTipo);
  const [tipoMenuOpen, setTipoMenuOpen] = useState(false);

  const selectedLabel = useMemo(
    () => tipoOptions.find((o) => o.value === selectedTipo)?.label ?? selectedTipo,
    [tipoOptions, selectedTipo]
  );

  const {
    data: documentos = [],
    isLoading,
    isError,
  } = useQuery<Documento[]>({
    queryKey: ["licitacion-documentos", licitacionId, familia],
    queryFn: () =>
      fetchWithAuth(
        `/api/v1/licitaciones/${licitacionId}/documentos?familia=${familia}`
      ),
    enabled: !!licitacionId,
  });

  const invalidateAll = useCallback(() => {
    // Invalida ambas listas (bases y apoyo) + el resumen de la licitación.
    queryClient.invalidateQueries({
      queryKey: ["licitacion-documentos", licitacionId],
    });
    queryClient.invalidateQueries({ queryKey: ["licitacion", licitacionId] });
  }, [queryClient, licitacionId]);

  const deleteMutation = useMutation({
    mutationFn: (docId: string) =>
      fetchWithAuth(`/api/v1/documentos/${docId}`, { method: "DELETE" }),
    onSuccess: invalidateAll,
  });

  const handleUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        await fetchWithAuth(
          `/api/v1/licitaciones/${licitacionId}/documentos?tipo=${encodeURIComponent(
            selectedTipo
          )}`,
          {
            method: "POST",
            headers: {},
            body: formData,
          }
        );
      }
      invalidateAll();
      showToast.success("Documento(s) subido(s). Procesando...");
    } catch {
      showToast.error("Error al subir el documento.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleProcessingComplete = useCallback(() => {
    invalidateAll();
    showToast.success("Documento procesado correctamente.");
  }, [invalidateAll]);

  const handleProcessingError = useCallback(
    (error: string | null) => {
      invalidateAll();
      showToast.error(error || "Error al procesar el documento.");
    },
    [invalidateAll]
  );

  return (
    <section className="space-y-3">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0">
          <Icon
            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
              familia === "bases" ? "text-blue-600" : "text-purple-600"
            }`}
          />
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Selector de tipo dentro de la familia */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setTipoMenuOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              aria-haspopup="menu"
              aria-expanded={tipoMenuOpen}
            >
              {selectedLabel}
              <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
            </button>
            {tipoMenuOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-10 overflow-hidden"
                onMouseLeave={() => setTipoMenuOpen(false)}
              >
                {tipoOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setSelectedTipo(opt.value);
                      setTipoMenuOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                      opt.value === selectedTipo
                        ? "bg-gray-50 font-semibold"
                        : ""
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Botón de subida */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            Subir documento
          </button>
        </div>
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        </div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 text-center text-sm text-red-700">
          <AlertCircle className="w-5 h-5 text-red-400 mx-auto mb-1" />
          Error cargando los documentos.
        </div>
      ) : documentos.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-md">
          <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 font-medium">
            Aún no hay documentos en este bloque
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Sube uno con el tipo correcto para que la IA lo use al generar tu
            propuesta.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2 text-[11px] font-medium text-gray-500 uppercase">
                  Nombre
                </th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-gray-500 uppercase hidden md:table-cell">
                  Tipo
                </th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-gray-500 uppercase hidden md:table-cell">
                  Tamaño
                </th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-gray-500 uppercase hidden md:table-cell">
                  Estado
                </th>
                <th className="text-left px-4 py-2 text-[11px] font-medium text-gray-500 uppercase hidden lg:table-cell">
                  Fecha
                </th>
                <th className="px-4 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {documentos.map((doc) => {
                const isProcessing =
                  doc.estado_procesamiento !== "COMPLETADO" &&
                  doc.estado_procesamiento !== "ERROR";

                return (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-gray-900 truncate block">
                            {doc.nombre}
                          </span>
                          {isProcessing && (
                            <DocumentProcessingProgress
                              docId={doc.id}
                              estado={doc.estado_procesamiento}
                              onComplete={handleProcessingComplete}
                              onError={handleProcessingError}
                            />
                          )}
                          {doc.estado_procesamiento === "ERROR" &&
                            doc.processing_error && (
                              <p
                                className="mt-1 text-xs text-red-600 break-words"
                                title={doc.processing_error}
                              >
                                {doc.processing_error.length > 140
                                  ? `${doc.processing_error.slice(0, 140)}…`
                                  : doc.processing_error}
                              </p>
                            )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-600 hidden md:table-cell">
                      {tipoOptions.find((o) => o.value === doc.tipo)?.label ??
                        doc.tipo}
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600 hidden md:table-cell">
                      {formatFileSize(doc.tamanio)}
                    </td>
                    <td className="px-4 py-2.5 hidden md:table-cell">
                      <div className="flex items-center gap-1.5">
                        {doc.estado_procesamiento === "COMPLETADO" && (
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
                        )}
                        {doc.estado_procesamiento === "ERROR" && (
                          <XCircle className="w-3.5 h-3.5 text-red-500" />
                        )}
                        {isProcessing && (
                          <Loader2 className="w-3.5 h-3.5 text-blue-500 animate-spin" />
                        )}
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            estadoProcessColors[doc.estado_procesamiento] ||
                            "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {doc.estado_procesamiento}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-gray-600 hidden lg:table-cell">
                      {formatDate(doc.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={async () => {
                            try {
                              const blob = await fetchBlobWithAuth(
                                `/api/v1/documentos/${doc.id}/download`
                              );
                              const url = window.URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = doc.nombre_original || doc.nombre;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              window.URL.revokeObjectURL(url);
                            } catch {
                              showToast.error("Error al descargar el documento");
                            }
                          }}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                          title="Descargar"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (confirm("¿Eliminar este documento?"))
                              deleteMutation.mutate(doc.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
